import * as AvatarPrimitive from '@radix-ui/react-avatar';
import {
  forwardRef,
  useMemo,
  type ComponentPropsWithoutRef,
  type ElementRef,
} from 'react';
import { avatarColor, cn, initials } from './utils';

export const Avatar = forwardRef<
  ElementRef<typeof AvatarPrimitive.Root>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      'relative flex h-9 w-9 shrink-0 overflow-hidden rounded-full',
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

export const AvatarImage = forwardRef<
  ElementRef<typeof AvatarPrimitive.Image>,
  ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn('aspect-square h-full w-full object-cover', className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

export interface AvatarFallbackProps
  extends ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback> {
  /** Full name — turned into up to 2 initials for display. */
  name?: string | null;
  /** Deterministic seed for the background colour; defaults to `name`. */
  seed?: string | number;
}

/**
 * AvatarFallback — coloured circle with initials, shown while the image
 * loads or when no image is provided. Colour is deterministic per-seed
 * via `avatarColor()` so the same user keeps the same tint everywhere.
 */
export const AvatarFallback = forwardRef<
  ElementRef<typeof AvatarPrimitive.Fallback>,
  AvatarFallbackProps
>(({ className, name, seed, children, ...props }, ref) => {
  const text = useMemo(() => {
    if (children != null && children !== '') return children;
    return initials(name);
  }, [children, name]);

  const colour = useMemo(() => {
    const s = seed ?? name ?? (typeof text === 'string' ? text : '?');
    return avatarColor(s);
  }, [seed, name, text]);

  return (
    <AvatarPrimitive.Fallback
      ref={ref}
      className={cn(
        'flex h-full w-full items-center justify-center rounded-full text-xs font-semibold uppercase text-white',
        colour,
        className,
      )}
      {...props}
    >
      {text}
    </AvatarPrimitive.Fallback>
  );
});
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;
